import { useEffect } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, IdCard, Wallet, Heart, CalendarCheck, MessagesSquare,
  Star, Flag, Ban, ImageIcon, ShieldCheck, Bell, Settings, ScrollText, BarChart3,
  Shield, ExternalLink, LogOut,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { OrnamentDivider } from "@/components/suffa/Ornaments";
import { AdminDashboard, AdminAnalytics, AdminSettings, AdminContent, AdminNotifications } from "@/components/admin/SectionsCore";
import { AdminUsers, AdminBlocked, AdminSecurity } from "@/components/admin/SectionsUsers";
import { AdminProfiles, AdminPhotos, AdminVerification } from "@/components/admin/SectionsProfiles";
import { AdminInterests, AdminMatches, AdminDiscussions } from "@/components/admin/SectionsEngage";
import { AdminPayments, AdminSuccessfulMatches, AdminReports } from "@/components/admin/SectionsFinance";
import { ADMIN_ROLES } from "@/pages/AdminLogin";
import "@/pages/Admin.css";

const SECTIONS = [
  ["dashboard", "Dashboard", LayoutDashboard],
  ["users", "Users", Users],
  ["profiles", "Profiles", IdCard],
  ["payments", "Registration Payments", Wallet],
  ["interests", "Interests", Heart],
  ["matches", "Matches & Meetings", CalendarCheck],
  ["discussions", "Matrimonial Discussion Board", MessagesSquare],
  ["successful", "Successful Matches", Star],
  ["reports", "Reports & Complaints", Flag],
  ["blocked", "Blocked / Suspended", Ban],
  ["photos", "Photos / Media", ImageIcon],
  ["verification", "Verification", ShieldCheck],
  ["notifications", "Notifications", Bell],
  ["settings", "Site Settings", Settings],
  ["content", "Legal & Content", ScrollText],
  ["analytics", "Reports & Analytics", BarChart3],
  ["security", "Admin & Security", Shield],
];

const SECTION_COMPONENTS = {
  dashboard: AdminDashboard,
  users: AdminUsers,
  profiles: AdminProfiles,
  payments: AdminPayments,
  interests: AdminInterests,
  matches: AdminMatches,
  discussions: AdminDiscussions,
  successful: AdminSuccessfulMatches,
  reports: AdminReports,
  blocked: AdminBlocked,
  photos: AdminPhotos,
  verification: AdminVerification,
  notifications: AdminNotifications,
  settings: AdminSettings,
  content: AdminContent,
  analytics: AdminAnalytics,
  security: AdminSecurity,
};

const SUBTITLES = {
  dashboard: "Live statistics from the Suffa Matrimony database.",
  users: "Search, review and manage member accounts.",
  profiles: "Approve, reject and moderate matrimonial profiles.",
  payments: "₹500 one-time profile registration fee records.",
  interests: "Every interest passes through admin review before delivery.",
  matches: "Cases created from mutual interests — meetings and follow-ups.",
  discussions: "Supervised matrimonial discussions — no private chatting.",
  successful: "Record finalized matches and the per-side service fee (offline).",
  reports: "User reports, profile reports and complaints.",
  blocked: "Accounts restricted by the admin team.",
  photos: "Review and moderate profile photographs.",
  verification: "Manage verification records — never automatic.",
  notifications: "Send notifications to users, families and match participants.",
  settings: "Platform configuration (Super Admin only).",
  content: "Manage legal and informational page content.",
  analytics: "Date-range reports across the platform.",
  security: "Admin accounts and the complete audit log.",
};

export default function AdminPanel() {
  const { user, authReady, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin Panel | Suffa Matrimony";
  }, []);

  // Server-side role checks protect every API; this guard protects every /admin/* route.
  if (!authReady) {
    return (
      <div className="ap-denied">
        <div className="ap-card"><p data-testid="ap-checking">Checking your session…</p></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  if (!ADMIN_ROLES.includes(user.role)) {
    return (
      <div className="ap-denied">
        <div className="ap-card" data-testid="ap-access-denied">
          <OrnamentDivider />
          <h1 className="ap-title" style={{ textAlign: "center" }}>Access Denied</h1>
          <p className="ap-sub" style={{ textAlign: "center", marginBottom: 18 }}>
            This area is restricted to authorized Suffa Matrimony administrators.
            Your account does not have admin privileges.
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <Link to="/admin/login" className="btn btn-solid btn-sm" data-testid="ap-denied-admin-login">
              Admin Login
            </Link>
            <Link to="/" className="btn btn-outline btn-sm">Back to Site</Link>
          </div>
        </div>
      </div>
    );
  }

  const path = location.pathname.replace(/\/+$/, "");
  if (path === "/admin") {
    return <Navigate to="/admin/dashboard" replace />;
  }
  const key = path.split("/")[2] || "dashboard";
  const section = SECTIONS.some(([k]) => k === key) ? key : "dashboard";
  const Active = SECTION_COMPONENTS[section];
  const go = (k) => navigate(`/admin/${k}`);

  return (
    <div className="ap-root" data-testid="admin-panel">
      <aside className="ap-sidebar">
        <div className="ap-sidebar-brand">
          <span>Suffa<small>MATRIMONY · ADMIN</small></span>
        </div>
        {SECTIONS.map(([skey, label, Icon]) => (
          <button
            key={skey}
            type="button"
            className={`ap-nav-item${skey === section ? " is-active" : ""}`}
            data-testid={`ap-nav-${skey}`}
            onClick={() => go(skey)}
          >
            <Icon size={16} aria-hidden="true" />
            {label}
          </button>
        ))}
        <button
          type="button"
          className="ap-nav-item"
          data-testid="ap-nav-logout"
          onClick={async () => {
            await logout();
            navigate("/admin/login", { replace: true });
          }}
        >
          <LogOut size={16} aria-hidden="true" />
          Logout
        </button>
        <div className="ap-sidebar-foot">
          <p style={{ margin: "0 0 8px" }}>{user.email}<br />Role: {user.role}</p>
          <Link to="/" className="btn btn-outline btn-sm" data-testid="ap-back-to-site" style={{ color: "#FDFBF7" }}>
            <ExternalLink size={12} /> View Site
          </Link>
        </div>
      </aside>
      <main className="ap-main">
        <div className="ap-topbar">
          <div>
            <h1 className="ap-title" data-testid="ap-section-title">
              {SECTIONS.find(([skey]) => skey === section)[1]}
            </h1>
            <p className="ap-sub">{SUBTITLES[section]}</p>
          </div>
        </div>
        <Active />
      </main>
    </div>
  );
}
